import React from 'react';
import { Box, SxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { typographyTokens } from '../../theme/typography-tokens';

export interface ChipLabelProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, keyof PounceStyleProps | 'sx'>,
    PounceStyleProps {
  /** Primary text content of the chip. */
  children: React.ReactNode;
  /** Additional styles to apply (combined with pounce props). */
  sx?: SxProps;
}

/**
 * Primary text span for chip components.
 *
 * Used in two ways:
 * 1. Implicit — `StaticChip`/`InteractiveChip` auto-wraps its `children` in
 *    `<ChipLabel>` when no chip primitives are detected in `children`.
 * 2. Explicit — when composing a chip with `<ChipAvatar>` / `<ChipField>` /
 *    `<ChipCounter>` / `<ChipDeleteButton>`, place `<ChipLabel>` alongside them
 *    to mark the primary text slot.
 *
 * Inherits color from the chip parent via `currentColor`; uses sans/medium
 * typography to match the chip's text style. Accepts pounceV2 style props
 * (`mr`, `mt`, `width`, …) and the standard `sx` escape hatch.
 *
 * @example
 * ```tsx
 * <InteractiveChip variant="primary" onClick={openProfile}>
 *   <ChipAvatar src={user.avatarUrl} />
 *   <ChipLabel>{user.name}</ChipLabel>
 * </InteractiveChip>
 * ```
 */
export const ChipLabel = React.forwardRef<HTMLSpanElement, ChipLabelProps>(function ChipLabel(
  { children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  return (
    <Box
      component="span"
      ref={ref}
      sx={
        {
          color: 'inherit',
          fontFamily: typographyTokens.fontFamilies.sans,
          fontWeight: typographyTokens.fontWeights.medium,
          ...pounceStyles,
          ...sx,
        } as SxProps
      }
      {...otherProps}
    >
      {children}
    </Box>
  );
});
